import React, { useState, useRef, useEffect } from "react";
import ParticipantsModal from "./ParticipantsModal";
import "./VideoGrid.css";

const getGridClass = (count) => {
  if (count <= 1) return "grid-1";
  if (count === 2) return "grid-2";
  if (count <= 4) return "grid-4";
  return "grid-6";
};

// Simple color hash for avatar backgrounds
function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = `hsl(${hash % 360}, 70%, 60%)`;
  return color;
}

// *** UPDATED ***: This component is now more robust.
const VideoTile = ({ participant, isSelf, localVideoRef }) => {
  const videoRef = useRef();
  const initial = participant.name?.charAt(0)?.toUpperCase() || '?';
  const avatarColor = stringToColor(participant.name || 'user');

  // Determine if video should be shown based on the flag AND an active video track.
  const videoTrack = participant.stream?.getVideoTracks().find(t => t.readyState === 'live');
  const showVideo = participant.videoOn && !!videoTrack;

  useEffect(() => {
    const targetRef = isSelf ? localVideoRef : videoRef;
    if (targetRef.current) {
      if (showVideo && participant.stream) {
        // The stream from the peer connection contains both audio and video.
        // Assigning it directly is the correct approach.
        targetRef.current.srcObject = participant.stream;
        targetRef.current.play().catch(err => console.warn('Video play failed:', err));
      } else {
        // If video is off or no stream, clear the srcObject.
        targetRef.current.srcObject = null;
      }
    }
  }, [participant.stream, showVideo, isSelf, localVideoRef]); // Depend on the stream object itself.

  return (
    <div className="relative flex flex-col items-center justify-center bg-black rounded-xl overflow-hidden w-full h-full video-tile">
      {showVideo ? (
        <video
          ref={isSelf ? localVideoRef : videoRef}
          autoPlay
          playsInline
          muted={participant.isSelf}
          className="w-full h-full object-cover"
        />
      ) : (
        <div
          className="flex flex-col items-center justify-center w-full h-full"
          style={{ background: avatarColor }}
        >
          <span className="text-4xl text-white font-bold">{initial}</span>
          <span className="text-xs mt-2 text-white">Camera off</span>
        </div>
      )}
      <div className="absolute bottom-2 left-2 text-white text-xs bg-black bg-opacity-50 rounded px-2 py-1">
        {participant.name}
      </div>
      <div className="absolute bottom-2 right-2 text-white p-1 bg-black bg-opacity-50 rounded-full text-lg">
        {participant.micOn ? '🎤' : '🔇'}
      </div>
    </div>
  );
};

const VideoGrid = ({ participants, localVideoRef }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const maxTiles = 6;
  const visibleParticipants = participants.slice(0, maxTiles);
  const extraCount = participants.length - maxTiles;

  return (
    <div className={`video-grid ${getGridClass(visibleParticipants.length)}`}>
      {visibleParticipants.map((p) => (
        <VideoTile 
            key={p.id} 
            participant={p} 
            isSelf={p.isSelf}
            localVideoRef={p.isSelf ? localVideoRef : null}
        />
      ))}
      {extraCount > 0 && (
        <div
          className="video-tile more-tile rounded-xl"
          onClick={() => setModalOpen(true)}
        >
          <div className="more-participants">+{extraCount} more</div>
        </div>
      )}
      {modalOpen && (
        <ParticipantsModal
          participants={participants}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
};

export default VideoGrid;
